import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import type { BoardState } from '../core/types';

import { VifAnswerTile } from './VifAnswerTile';
import './VifGridBoard.scss';
import { VifGridCell } from './VifGridCell';
import { cellId, cellIndex, resolveDrop } from './dragIds';

/**
 * Minimum pointer travel before a drag starts, in px. Without it a plain tap
 * registers as a drag and the tile never settles - the canvas game has the
 * same effective behaviour because it only trades on a cell change.
 */
const DRAG_ACTIVATION_DISTANCE = 5;

export interface VifGridBoardProps {
  state: BoardState;
  onDrop: (from: number, to: number) => void;
  /**
   * Prefix rendered before every answer. Legacy `preString` - flag 8 uses
   * `'x = '`. Empty for the addition flags.
   */
  preString?: string;
  /** Legacy `scaleVar`: some flags render answers at double size. */
  large?: boolean;
  /**
   * Whether the problems are showing. The canvas game blanks every tile until
   * START is pressed, so the board defaults to concealed.
   */
  revealed?: boolean;
  /** Tapping a solved, revealed tile. Auditory Memory boards only. */
  onSpeak?: (index: number) => void;
}

export function VifGridBoard({
  state,
  onDrop,
  preString = '',
  large = false,
  revealed = true,
  onSpeak,
}: VifGridBoardProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveIndex(cellIndex(String(event.active.id)));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveIndex(null);

      const drop = resolveDrop(
        String(event.active.id),
        event.over ? String(event.over.id) : null,
      );

      if (drop) {
        onDrop(drop.from, drop.to);
      }
    },
    [onDrop],
  );

  const handleDragCancel = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const { rows, cols } = state.config;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToWindowEdges]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div
        className="vif-grid-board"
        style={
          {
            '--vif-grid-cols': cols,
            '--vif-grid-rows': rows,
          } as React.CSSProperties
        }
        role="group"
        aria-label="Very Important Facts board"
      >
        {state.equations.map((equation, index) => (
          <VifGridCell
            key={cellId(index)}
            id={cellId(index)}
            index={index}
            equation={equation}
            answer={state.placed[index] ?? ''}
            locked={state.locked[index] ?? false}
            rejecting={state.rejectingIndex === index}
            dragging={activeIndex === index}
            disabled={state.rejectingIndex !== null || state.complete}
            revealed={revealed}
            preString={preString}
            large={large}
            onSpeak={onSpeak}
          />
        ))}
      </div>

      {/*
       * Portalled to the body on purpose. `DragOverlay` is `position: fixed`
       * with `top`/`left` copied from the dragged tile's *viewport* rect, so it
       * only lands under the cursor while the viewport is its containing block.
       * `.vif-rules-view` sets `container-type: inline-size`, which applies
       * layout containment and therefore becomes the containing block for fixed
       * descendants - leaving the overlay rendered in place displaced from the
       * pointer by the board's own page offset.
       */}
      {createPortal(
        <DragOverlay className="vif-drag-overlay" dropAnimation={null}>
          {activeIndex === null ? null : (
            <VifAnswerTile
              answer={state.placed[activeIndex] ?? ''}
              preString={preString}
              large={large}
              overlay
            />
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
